import { useGetDirectEmissionsItems } from "hooks/query/questionnaire/direct-emissions";
import { useGetIndirectEmissionsItems } from "hooks/query/questionnaire/indirect-emissions";
import { useGetPurchasedElectricity, useGetOnsiteGeneration } from "hooks/query/questionnaire/scope-two-v2";
import { useGetBusinessTravelItems } from "hooks/query/questionnaire/categories/business-travel";
import { useGetEmployeeCommutingItems } from "hooks/query/questionnaire/categories/employee-commuting";
import { useGetCapitalGoodsItems } from "hooks/query/questionnaire/categories/capitalGoods";
import { useGetPurchasedProductItems } from "hooks/query/questionnaire/categories/purchasedGoodsAndServices";

// A single page-1-row read, purely to read `.totalElements` off the response — the same trick
// OrganizationQuestionnaire.jsx already uses for its Scope 1/2 "has data" dots.
const COUNT_ONLY_PARAMS = { page: 0, size: 1 };

/**
 * How many rows already exist in a data task's destination section, for the Commit tab's "this section
 * already has N rows" banner — so the user has that context before choosing Add vs Replace.
 *
 * No new backend endpoint: every real `AiIngestTarget` (see backend's AiIngestTarget.java) already has an
 * existing task-grid list hook returning a Spring `Page`, which already carries `totalElements`. This hook
 * is just a lookup table dispatching to the right one and reading that field back out.
 *
 * React hooks can't be called conditionally, so all eight underlying hooks below fire on every render —
 * `enabled` gates all but the one matching the current target, same conditional-`enabled` pattern
 * OrganizationQuestionnaire.jsx already uses for its own has-data flags.
 *
 * Returns `{ count, isLoading }`. `count` is `null` while loading, or for a target this hook does not (yet)
 * recognise — new Scope 3 targets are added to `AiIngestTarget` reasonably rarely, and a missing entry here
 * should degrade to "say nothing" rather than throw.
 */
export function useExistingRowCount(questionnaireId, aiIngestTarget) {
  const enabled = Boolean(questionnaireId) && Boolean(aiIngestTarget);
  const gate = (target) => ({ enabled: enabled && aiIngestTarget === target, cacheTime: 0, refetchOnWindowFocus: false });

  const stationary = useGetDirectEmissionsItems(
    { questionnaireId, type: "stationary", queryParams: COUNT_ONLY_PARAMS },
    gate("SCOPE_1_STATIONARY")
  );
  const mobile = useGetDirectEmissionsItems(
    { questionnaireId, type: "mobile", queryParams: COUNT_ONLY_PARAMS },
    gate("SCOPE_1_MOBILE")
  );
  const refrigerant = useGetDirectEmissionsItems(
    { questionnaireId, type: "refrigerant", queryParams: COUNT_ONLY_PARAMS },
    gate("SCOPE_1_REFRIGERANT")
  );
  const electricityV1 = useGetIndirectEmissionsItems(
    { questionnaireId, type: "electricity", queryParams: COUNT_ONLY_PARAMS },
    gate("SCOPE_2_V1_ELECTRICITY")
  );
  const purchasedElectricityV2 = useGetPurchasedElectricity(
    { questionnaireId, queryParams: COUNT_ONLY_PARAMS },
    gate("SCOPE_2_V2_PURCHASED_ELECTRICITY")
  );
  const onsiteGenerationV2 = useGetOnsiteGeneration(
    { questionnaireId, queryParams: COUNT_ONLY_PARAMS },
    gate("SCOPE_2_V2_ONSITE_GENERATION")
  );
  const businessTravel = useGetBusinessTravelItems(
    { questionnaireId, queryParams: COUNT_ONLY_PARAMS },
    gate("SCOPE_3_BUSINESS_TRAVEL")
  );
  const employeeCommuting = useGetEmployeeCommutingItems(
    { questionnaireId, queryParams: COUNT_ONLY_PARAMS },
    gate("SCOPE_3_EMPLOYEE_COMMUTING")
  );
  const capitalGoods = useGetCapitalGoodsItems(
    { questionnaireId, queryParams: COUNT_ONLY_PARAMS },
    gate("SCOPE_3_CAPITAL_GOODS")
  );
  const purchasedGoods = useGetPurchasedProductItems(
    { questionnaireId, queryParams: COUNT_ONLY_PARAMS },
    gate("SCOPE_3_PURCHASED_GOODS")
  );

  const byTarget = {
    SCOPE_1_STATIONARY: stationary,
    SCOPE_1_MOBILE: mobile,
    SCOPE_1_REFRIGERANT: refrigerant,
    SCOPE_2_V1_ELECTRICITY: electricityV1,
    SCOPE_2_V2_PURCHASED_ELECTRICITY: purchasedElectricityV2,
    SCOPE_2_V2_ONSITE_GENERATION: onsiteGenerationV2,
    SCOPE_3_BUSINESS_TRAVEL: businessTravel,
    SCOPE_3_EMPLOYEE_COMMUTING: employeeCommuting,
    SCOPE_3_CAPITAL_GOODS: capitalGoods,
    SCOPE_3_PURCHASED_GOODS: purchasedGoods,
  };

  const active = byTarget[aiIngestTarget];
  if (!active) return { count: null, isLoading: false };

  return {
    count: active.data?.totalElements ?? null,
    isLoading: active.isLoading,
  };
}

export default useExistingRowCount;
